import { useCallback, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { EXPERIENCE_TYPES, LISTING_TAB_BY_ROUTE } from "constants/experiences"

const ALL_TYPES = "all"
const getRouteOfListingTab = (tab) => Object.keys(LISTING_TAB_BY_ROUTE).find((route) => LISTING_TAB_BY_ROUTE[route] === tab)

export const useExperienceFilters = (initialTab = "published") => {
  const [selectedType, setSelectedType] = useState("private")
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const categoriesParam = searchParams.get("categories") || ""
  const durationTypesParam = searchParams.get("duration_types") || ""
  // The Private / Ticketed chip is kept in the URL like the filters, so it survives a reload.
  const typeParam = searchParams.get("type")
  const activeTab = Object.values(EXPERIENCE_TYPES).includes(typeParam) ? typeParam : ALL_TYPES
  // `listings` is how the blackout surfaces deep-link here ("View" on an affected-listings row).
  // The named listings can sit in any status, so such a link opens on the "All" tab rather than
  // the default Published one, where a draft listing would silently drop out of the result.
  const listingsParam = searchParams.get("listings") || ""
  // The status tab too, as ?route= — which is also how the wizard's exits name the tab to return to.
  const routeParam = searchParams.get("route")
  const defaultListingTab = listingsParam ? LISTING_TAB_BY_ROUTE.all : initialTab
  const activeListingTab = Object.prototype.hasOwnProperty.call(LISTING_TAB_BY_ROUTE, routeParam) ? LISTING_TAB_BY_ROUTE[routeParam] : defaultListingTab

  const selectedCategoryIds = useMemo(
    () => (categoriesParam ? categoriesParam.split(",").map(Number).filter(Boolean) : []),
    [categoriesParam]
  )
  const selectedDurationTypes = useMemo(
    () => (durationTypesParam ? durationTypesParam.split(",").filter(Boolean) : []),
    [durationTypesParam]
  )
  // Listing ids stay strings: they arrive from the blackout endpoints, which serialize ids as
  // strings, and are compared against the chip values built from the same param.
  const selectedListingIds = useMemo(
    () => (listingsParam ? listingsParam.split(",").filter(Boolean) : []),
    [listingsParam]
  )
  const activeFilterCount = selectedCategoryIds.length + selectedDurationTypes.length + selectedListingIds.length

  const buildFilterQuery = useCallback(() => {
    const q = {}
    if (activeTab !== ALL_TYPES) q.experience_type_eq = activeTab
    if (selectedCategoryIds.length > 0) q.category_ids_in = selectedCategoryIds
    if (selectedDurationTypes.length > 0) q.duration_type_in = selectedDurationTypes
    if (selectedListingIds.length > 0) q.id_in = selectedListingIds
    return q
  }, [activeTab, selectedCategoryIds, selectedDurationTypes, selectedListingIds])

  const applyFilters = useCallback(
    // `listingIds` defaults to what is already applied so the category/duration dropdown, which
    // knows nothing about it, does not drop the deep-linked listing filter on every apply.
    ({ categoryIds = [], durationTypes = [], listingIds = selectedListingIds }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (categoryIds.length > 0) next.set("categories", categoryIds.join(","))
          else next.delete("categories")
          if (durationTypes.length > 0) next.set("duration_types", durationTypes.join(","))
          else next.delete("duration_types")
          if (listingIds.length > 0) next.set("listings", listingIds.join(","))
          else next.delete("listings")
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams, selectedListingIds]
  )

  const clearFilters = useCallback(() => applyFilters({ categoryIds: [], durationTypes: [], listingIds: [] }), [applyFilters])

  const removeCategoryFilter = useCallback(
    (id) => applyFilters({ categoryIds: selectedCategoryIds.filter((v) => v !== id), durationTypes: selectedDurationTypes }),
    [applyFilters, selectedCategoryIds, selectedDurationTypes]
  )

  const removeDurationTypeFilter = useCallback(
    (value) => applyFilters({ categoryIds: selectedCategoryIds, durationTypes: selectedDurationTypes.filter((v) => v !== value) }),
    [applyFilters, selectedCategoryIds, selectedDurationTypes]
  )

  const handleTabChange = useCallback(
    (tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (tab === ALL_TYPES) next.delete("type")
          else next.set("type", tab)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const handleListingTabChange = useCallback(
    (tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set("route", getRouteOfListingTab(tab) ?? tab)
          return next
        },
        { replace: true }
      )
      setSelectedType("")
    },
    [setSearchParams]
  )

  const handleFilterIconClick = useCallback(() => {
    setIsFilterDropdownOpen((prev) => !prev)
  }, [])

  const handleCloseFilterDropdown = useCallback(() => {
    setIsFilterDropdownOpen(false)
  }, [])

  return {
    activeTab,
    activeListingTab,
    selectedType,
    isFilterDropdownOpen,
    handleTabChange,
    handleListingTabChange,
    setSelectedType,
    handleFilterIconClick,
    handleCloseFilterDropdown,
    selectedCategoryIds,
    selectedDurationTypes,
    selectedListingIds,
    activeFilterCount,
    buildFilterQuery,
    applyFilters,
    clearFilters,
    removeCategoryFilter,
    removeDurationTypeFilter
  }
}
